import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  TextInput,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

// ============================================================
// SVG Icon Components
// ============================================================

const CheckCircleIcon = ({ size = 20, color = '#16A34A' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <Path d="M8 12L11 15L16 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const XCircleIcon = ({ size = 20, color = '#DC2626' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <Path d="M15 9L9 15M9 9L15 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ClockIcon = ({ size = 16, color = '#EAB308' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <Path d="M12 6V12L16 14" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const CloseIcon = ({ size = 24, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// ============================================================
// Props Interface
// ============================================================

interface PendingItem {
  id: number;
  title?: string;
  name?: string;
  description?: string;
  submitted_by_details?: {
    id: number;
    full_name: string;
    email: string;
  };
  submitted_at?: string;
}

interface PendingApprovalSectionProps {
  title: string;
  items: PendingItem[];
  onApprove: (id: number) => Promise<void>;
  onReject: (id: number, reason: string) => Promise<void>;
  onRefresh: () => void;
}

// ============================================================
// Main Component
// ============================================================

export default function PendingApprovalSection({
  title,
  items,
  onApprove,
  onReject,
  onRefresh,
}: PendingApprovalSectionProps) {
  const { t } = useLanguage();
  const { colors, isDark } = useTheme();
  const { showNotification } = useNotification();

  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    sectionBg: isDark ? '#1F2937' : '#FFF9E6',
    sectionBorder: isDark ? '#374151' : '#EAB308',
  };

  const handleApprove = async (itemId: number) => {
    try {
      setLoading(true);
      await onApprove(itemId);
      showNotification(t('approval.approvalSuccess'), 'success');
      onRefresh();
    } catch (error: any) {
      console.error('Error approving item:', error);
      showNotification(
        error.response?.data?.error || t('approval.approvalError'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = (itemId: number) => {
    setSelectedItemId(itemId);
    setRejectionReason('');
    setRejectModalVisible(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedItemId) return;

    if (!rejectionReason.trim()) {
      showNotification(t('approval.pleaseEnterReason'), 'error');
      return;
    }

    try {
      setLoading(true);
      await onReject(selectedItemId, rejectionReason);
      showNotification(t('approval.rejectionSuccess'), 'success');
      setRejectModalVisible(false);
      setSelectedItemId(null);
      setRejectionReason('');
      onRefresh();
    } catch (error: any) {
      console.error('Error rejecting item:', error);
      showNotification(
        error.response?.data?.error || t('approval.rejectionError'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {/* Pending Requests Section */}
      <View
        style={[
          styles.pendingSection,
          {
            backgroundColor: themeColors.sectionBg,
            borderLeftColor: themeColors.sectionBorder,
          },
        ]}
      >
        {/* Header */}
        <View style={styles.pendingSectionHeader}>
          <View style={styles.pendingHeaderContent}>
            <ClockIcon size={20} color="#EAB308" />
            <Text style={[styles.pendingSectionTitle, { color: themeColors.textPrimary }]}>
              {title} ({items.length})
            </Text>
          </View>
        </View>

        {/* Pending Items List */}
        <View style={styles.pendingItemsList}>
          {items.map((item) => (
            <View
              key={item.id}
              style={[
                styles.pendingItemCard,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
              ]}
            >
              {/* Item Info */}
              <View style={styles.pendingItemInfo}>
                <Text style={[styles.pendingItemTitle, { color: themeColors.textPrimary }]} numberOfLines={2}>
                  {item.title || item.name}
                </Text>
                {item.description && (
                  <Text style={[styles.pendingItemDescription, { color: themeColors.textSecondary }]} numberOfLines={2}>
                    {item.description}
                  </Text>
                )}
                {item.submitted_by_details && item.submitted_at && (
                  <Text style={[styles.pendingItemMeta, { color: themeColors.textMuted }]}>
                    {t('approval.submittedBy')}: {item.submitted_by_details.full_name} •{' '}
                    {new Date(item.submitted_at).toLocaleDateString('ar-AE')}
                  </Text>
                )}
              </View>

              {/* Action Buttons */}
              <View style={styles.pendingItemActions}>
                <TouchableOpacity
                  style={[styles.approveButton, loading && styles.buttonDisabled]}
                  onPress={() => handleApprove(item.id)}
                  disabled={loading}
                >
                  {loading ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <>
                      <CheckCircleIcon size={18} color="#FFFFFF" />
                      <Text style={styles.approveButtonText}>{t('approval.approve')}</Text>
                    </>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.rejectButton, loading && styles.buttonDisabled]}
                  onPress={() => handleRejectClick(item.id)}
                  disabled={loading}
                >
                  <XCircleIcon size={18} color="#FFFFFF" />
                  <Text style={styles.rejectButtonText}>{t('approval.reject')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Rejection Reason Modal */}
      <Modal
        visible={rejectModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setRejectModalVisible(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setRejectModalVisible(false)}
        >
          <Pressable
            style={[styles.modalContent, { backgroundColor: themeColors.card }]}
            onPress={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: themeColors.textPrimary }]}>
                {t('approval.rejectionReason')}
              </Text>
              <TouchableOpacity
                onPress={() => setRejectModalVisible(false)}
                style={styles.modalCloseButton}
              >
                <CloseIcon size={24} color={themeColors.textMuted} />
              </TouchableOpacity>
            </View>

            {/* Rejection Reason Input */}
            <TextInput
              style={[
                styles.reasonInput,
                {
                  backgroundColor: isDark ? '#1F2937' : '#F9FAFB',
                  color: themeColors.textPrimary,
                  borderColor: themeColors.cardBorder,
                },
              ]}
              multiline
              numberOfLines={4}
              value={rejectionReason}
              onChangeText={setRejectionReason}
              placeholder={t('approval.enterRejectionReason')}
              placeholderTextColor={themeColors.textMuted}
              textAlign="right"
            />

            {/* Modal Actions */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalCancelButton, { borderColor: themeColors.cardBorder }]}
                onPress={() => setRejectModalVisible(false)}
              >
                <Text style={[styles.modalCancelButtonText, { color: themeColors.textPrimary }]}>
                  {t('common.cancel')}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.modalConfirmButton, loading && styles.buttonDisabled]}
                onPress={handleRejectConfirm}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.modalConfirmButtonText}>{t('approval.reject')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  pendingSection: {
    backgroundColor: '#FFF9E6',
    borderLeftWidth: 4,
    borderLeftColor: '#EAB308',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  pendingSectionHeader: {
    marginBottom: 16,
  },
  pendingHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pendingSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.semiBold,
    color: '#414042',
  },
  pendingItemsList: {
    gap: 12,
  },
  pendingItemCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 16,
    gap: 12,
  },
  pendingItemInfo: {
    gap: 8,
  },
  pendingItemTitle: {
    fontSize: 14,
    fontFamily: FONTS.semiBold,
    color: '#414042',
    textAlign: 'right',
  },
  pendingItemDescription: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    lineHeight: 20,
  },
  pendingItemMeta: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'right',
  },
  pendingItemActions: {
    flexDirection: 'row',
    gap: 8,
  },
  approveButton: {
    flex: 1,
    backgroundColor: '#16A34A',
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  approveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  rejectButton: {
    flex: 1,
    backgroundColor: '#DC2626',
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  rejectButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  buttonDisabled: {
    opacity: 0.6,
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    gap: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.semiBold,
    color: '#414042',
  },
  modalCloseButton: {
    padding: 4,
  },
  reasonInput: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 6,
    paddingVertical: 12,
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  modalConfirmButton: {
    flex: 1,
    backgroundColor: '#DC2626',
    borderRadius: 6,
    paddingVertical: 12,
    alignItems: 'center',
  },
  modalConfirmButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
});
